/**
 * WMS product link/create facade: search-and-link, create-missing, link, unlink
 * by provider (Picqer vs ChannelDock). Same response shapes for PurchaseOrderSection.
 */
import { supabase } from '../lib/supabase';
import type { WmsProvider } from './wmsOrdersService';
import { PicqerService } from './picqerService';

export interface SearchAndLinkResult {
  success: boolean;
  data?: {
    createdCount: number;
    errorCount: number;
    results: Array<{
      productId: number;
      success: boolean;
      picqerProductId?: number;
      companyProductId?: number;
      error?: string;
    }>;
  };
  error?: string;
}

export interface CreateMissingResult {
  success: boolean;
  data?: {
    createdCount: number;
    errorCount: number;
    results: Array<{
      productId: number;
      success: boolean;
      picqerProductId?: number;
      companyProductId?: number;
      error?: string;
    }>;
  };
  error?: string;
}

const CHANNELDOCK_IMPORT_FN = 'channeldock-product-import';

export const WmsProductLinkService = {
  async searchAndLinkExistingProducts(
    bookingId: number,
    productIds: number[],
    provider: WmsProvider
  ): Promise<SearchAndLinkResult> {
    if (provider === 'channeldock') {
      try {
        const { data, error } = await supabase.functions.invoke(CHANNELDOCK_IMPORT_FN, {
          body: { action: 'search-and-link-existing-products', bookingId, productIds },
        });
        if (error) throw error;
        const out = data as SearchAndLinkResult;
        return out?.success ? out : { success: false, error: (out as { error?: string }).error ?? 'Failed to link' };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Failed to search and link' };
      }
    }
    return PicqerService.searchAndLinkExistingProducts(bookingId, productIds);
  },

  async createMissingProductsForBooking(
    bookingId: number,
    productIds: number[],
    provider: WmsProvider
  ): Promise<CreateMissingResult> {
    if (provider === 'channeldock') {
      try {
        const { data, error } = await supabase.functions.invoke(CHANNELDOCK_IMPORT_FN, {
          body: { action: 'create-missing-products', bookingId, productIds },
        });
        if (error) throw error;
        const out = data as CreateMissingResult;
        return out ?? { success: false, error: 'No response' };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Failed to create products' };
      }
    }
    return PicqerService.createMissingProductsForBooking(bookingId, productIds);
  },

  async linkProduct(
    companyId: number,
    wmsProductId: number,
    companyProductId: number,
    provider: WmsProvider
  ): Promise<{ success: boolean; error?: string }> {
    if (provider === 'channeldock') {
      try {
        const { data, error } = await supabase.functions.invoke(CHANNELDOCK_IMPORT_FN, {
          body: {
            action: 'link',
            companyId,
            linkRequests: [{ existingProductId: companyProductId, channeldockProductId: wmsProductId }],
          },
        });
        if (error) throw error;
        const out = data as { success?: boolean; error?: string };
        return out?.success ? { success: true } : { success: false, error: out?.error ?? 'Failed to link' };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Failed to link product' };
      }
    }
    return PicqerService.linkProduct(companyId, wmsProductId, companyProductId);
  },

  async unlinkProduct(
    companyId: number,
    companyProductId: number,
    provider: WmsProvider
  ): Promise<{ success: boolean; error?: string }> {
    const column = provider === 'channeldock' ? 'channeldock_product_id' : 'picqer_product_id';
    try {
      const { error } = await supabase
        .from('company_product')
        .update({ [column]: null })
        .eq('id', companyProductId)
        .eq('company_id', companyId);
      if (error) throw error;
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Failed to unlink product' };
    }
  },

  /**
   * Clears WMS link (Picqer or ChannelDock column) on every company_product that matches
   * this booking's lines by EAN — same scope as PO product analysis.
   */
  async unlinkAllBookingProductsFromWms(
    bookingId: number,
    companyId: number,
    provider: WmsProvider
  ): Promise<{ success: boolean; unlinkedCount: number; error?: string }> {
    const column = provider === 'channeldock' ? 'channeldock_product_id' : 'picqer_product_id';
    try {
      const { data: bookingProducts, error: bpErr } = await supabase
        .from('booking_product')
        .select('ean')
        .eq('booking_id', bookingId);
      if (bpErr) throw bpErr;

      const eanSet = new Set<string>();
      for (const row of bookingProducts ?? []) {
        if (row.ean != null && String(row.ean).trim() !== '') {
          eanSet.add(String(row.ean).trim());
        }
      }
      if (eanSet.size === 0) {
        return { success: true, unlinkedCount: 0 };
      }

      const eans = [...eanSet];
      const { data: companyProducts, error: cpErr } = await supabase
        .from('company_product')
        .select('id')
        .eq('company_id', companyId)
        .in('ean', eans)
        .not(column, 'is', null);
      if (cpErr) throw cpErr;

      const ids = [...new Set((companyProducts ?? []).map((r) => r.id))];
      if (ids.length === 0) {
        return { success: true, unlinkedCount: 0 };
      }

      const { error: upErr } = await supabase
        .from('company_product')
        .update({ [column]: null })
        .eq('company_id', companyId)
        .in('id', ids);
      if (upErr) throw upErr;

      return { success: true, unlinkedCount: ids.length };
    } catch (err) {
      return {
        success: false,
        unlinkedCount: 0,
        error: err instanceof Error ? err.message : 'Failed to unlink products',
      };
    }
  },

  /** Link one booking product to a chosen WMS product (manual pick from list). Creates or updates company_product. */
  async linkBookingProductToWms(
    bookingProductId: number,
    wmsProductId: number,
    provider: WmsProvider
  ): Promise<{ success: boolean; error?: string }> {
    const fn = provider === 'channeldock' ? CHANNELDOCK_IMPORT_FN : 'picqer-product-import';
    try {
      const { data, error } = await supabase.functions.invoke(fn, {
        body: { action: 'link-booking-product-to-wms', bookingProductId, wmsProductId },
      });
      if (error) throw error;
      const out = data as { success?: boolean; error?: string };
      return out?.success ? { success: true } : { success: false, error: out?.error ?? 'Failed to link' };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Failed to link product' };
    }
  },
};
